<template>
  <div class="jim-scheduling-container price-sheet-page">

    <!-- Page header row (matches JIMScheduling vibe) -->
    <div class="page-header-row">
      <div class="page-header-left"></div>
      <h1 class="page-heading">PRICE SHEETS</h1>
      <div class="page-header-right"></div>
    </div>

    <div class="main-content">

      <!-- ✅ LEFT SIDE CONTROLS (Pablo style) -->
      <div class="left-side-section">

        <div class="control" style="width:100%;">
          <label>Customer Number</label>
          <input
            v-model="custNo"
            type="text"
            placeholder="Enter customer # (ex: 12601)"
            @keyup.enter="loadPriceSheet"
          />
        </div>

        <button class="plan-it-btn" :disabled="loading || !custNo" @click="loadPriceSheet">
          {{ loading ? "Loading..." : "Load Price Sheet" }}
        </button>

        <div class="print-name-toggle">
          <div class="print-name-label">Print Name Toggle</div>

          <div class="print-name-buttons">
            <button
              class="plan-it-btn print-name-btn"
              :class="{ active: printNameMode === 'parent' }"
              :disabled="!priceSheetLoaded"
              @click="printNameMode = 'parent'"
            >
              Parent
            </button>

            <button
              class="plan-it-btn print-name-btn"
              :class="{ active: printNameMode === 'child' }"
              :disabled="!priceSheetLoaded"
              @click="printNameMode = 'child'"
            >
              Customer
            </button>
          </div>
        </div>

        <button class="plan-it-btn" :disabled="!priceSheetLoaded" @click="openPrintView">
          Print
        </button>

        <button class="plan-it-btn" :disabled="!priceSheetLoaded" @click="exportCSV">
          Export CSV
        </button>

        <button class="plan-it-btn" :disabled="!priceSheetLoaded" @click="openOpenOrdersModal()">
          Open Orders
        </button>

        <button class="plan-it-btn" :disabled="!priceSheetLoaded" @click="openSalesHistoryModal()">
          Sales History
        </button>


        <button class="plan-it-btn" :disabled="!priceSheetLoaded" @click="openCloneModal">
          Clone Pricing
        </button>

        <button class="plan-it-btn" :disabled="!priceSheetLoaded" @click="openUpdatePricingModal">
          Update Pricing
        </button>

        <button
          class="plan-it-btn"
          :disabled="massItemPreviewLoading || massItemSubmitting"
          @click="openMassItemPricingModal"
        >
          Mass Item Price
        </button>

        <button class="plan-it-btn" :disabled="!priceSheetLoaded" @click="openPricingDatesModal">
          Dates / Delete
        </button>



        <!-- ✅ Error -->
        <div v-if="error" class="panel error-box" style="margin-top:10px;">
          {{ error }}
        </div>

      </div>

      <!-- ✅ RIGHT SIDE CONTENT (scrollable) -->
      <div class="right-side-section">

        <!-- Customer Summary -->
        <div v-if="priceSheetLoaded" class="summary-bar">
          <div class="summary-item">
            <div class="summary-label">Parent</div>
            <div class="summary-value">{{ customerName }}</div>
          </div>

          <div class="summary-item" v-if="customerCityState">
            <div class="summary-label">City / State</div>
            <div class="summary-value">{{ customerCityState }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Cust #</div>
            <div class="summary-value">{{ sheetData.cust_no }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Customer Name</div>
            <div class="summary-value">{{ childCustomerName }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Pages</div>
            <div class="summary-value">{{ sheetData.pages?.length || 0 }}</div>
          </div>
        </div>

        <!-- Notes -->
        <div v-if="priceSheetLoaded" class="panel notes-panel">
          <div class="notes-title">Price Sheet Notes</div>
          <div class="notes-body">
            {{ notesText }}
          </div>
        </div>

        <!-- ✅ SCROLLABLE CONTENT WRAPPER -->
        <div v-if="priceSheetLoaded" class="sheet-scroll-container">
          <div class="sheet-wrapper">

            <!-- Pages -->
            <div
              v-for="page in sheetData.pages"
              :key="page.page_no"
              class="sheet-page panel"
            >
              <div class="sheet-page-header">
                <h2>Page {{ page.page_no }}</h2>
                <div class="small-muted">
                  Rows: {{ page.rows?.length || 0 }}
                </div>
              </div>

              <div class="table-scroll">
                <table class="price-table">
                  <thead>
                    <tr>
                      <th class="sticky-col">Product Group</th>
                      <th class="desc-col">Description</th>
                      <th class="pkg-col">Pkg.</th>
                      <th
                        v-for="d in (page.diameters || [])"
                        :key="d"
                        class="diam-col"
                      >
                        {{ d }}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr v-for="row in page.rows" :key="row.product_group">
                      <td class="sticky-col group-cell">{{ row.product_group }}</td>
                      <td class="desc-col">{{ row.description || "" }}</td>
                      <td class="pkg-col">{{ row.pkg || "" }}</td>

                      <td
                        v-for="d in (page.diameters || [])"
                        :key="d"
                        class="price-cell clickable-price"
                        @click="row.prices[d] != null && openItemModal(row, d)"
                        :title="row.prices[d] != null ? 'Click for YTD Sales / Orders / History' : ''"
                        :data-item="row.items?.[d] || ''"
                      >
                        <span v-if="row.prices[d] != null">
                          {{ formatPrice(row.prices[d]) }}
                        </span>
                        <span v-else class="dash">-</span>
                      </td>

                    </tr>

                    <!-- ✅ Prepaid Freight Multipliers Row -->
                    <tr v-if="freightMultipliersExist" class="freight-row">
                      <td class="sticky-col group-cell">Prepaid Freight Multipliers</td>
                      <td class="desc-col" colspan="2"></td>
                      <td :colspan="(page.diameters || []).length" class="freight-cell">
                        <div class="freight-line">
                          <span class="freight-label">Full T/L</span>
                          <span class="freight-value">{{ freightMultipliers.fullTL }}</span>

                          <span class="sep">•</span>

                          <span class="freight-label">$20,000x</span>
                          <span class="freight-value">{{ freightMultipliers.x20000 }}</span>

                          <span class="sep">•</span>

                          <span class="freight-label">$12,000x</span>
                          <span class="freight-value">{{ freightMultipliers.x12000 }}</span>

                          <span class="sep">•</span>

                          <span class="freight-label">$8,000x</span>
                          <span class="freight-value">{{ freightMultipliers.x8000 }}</span>

                          
                        </div>
                      </td>
                    </tr>

                    <tr v-if="!page.rows || page.rows.length === 0">
                      <td :colspan="3 + (page.diameters || []).length" class="empty-row">
                        No pricing rows found for this page.
                      </td>
                    </tr>
                  </tbody>
                </table>

                <!-- ✅ Footer -->
                <div class="print-footer">
                  <div v-if="page.page_no === 1" class="footer-text">
                    A full truck load consists of 24 pallets or 3,250 cubic feet. We encourage customers to purchase in full pallet quantities. Quantities for less than full pallets of products packaged in cartons will be shipped loose and not subject to receiving discrepancies. Please consult the JPL Packaging Guide for individual weights, cubes, bundle and pallet quantities.
                  </div>

                  <div v-else-if="page.page_no === 2" class="footer-text footer-grid">
                    <div class="footer-line">
                      AMF05, MF05, and AMR products are produced on the west coast only, and shipped pre-pay and charge to the Central and Eastern regions.
                    </div>

                    <div class="footer-row"><div class="k">Taxes</div><div class="v">Federal state and municipal taxes (if applicable) are not included</div></div>
                    <div class="footer-row"><div class="k">Returns</div><div class="v">Material may not be returned, credited or replaced without prior written authorization. Products returned must be in good resale condition, in their original unopened package and be less than 2 yrs old. Return freight will be the responsibility of the customer. A 15% restocking fee will be charged.</div></div>
                    <div class="footer-row"><div class="k">Backorders</div><div class="v">If a line item ships 90% complete there will be no backorder unless instructed otherwise.</div></div>
                    <div class="footer-row"><div class="k">Invoices</div><div class="v">Will be per piece.</div></div>
                    <div class="footer-row"><div class="k">Shipping</div><div class="v">See JPL net price sheet for freight pre paid (FFA) minimum requirements.</div></div>
                    <div class="footer-row"><div class="k">Minimum</div><div class="v">A $15 handling charge will be added to orders less than $100 net.</div></div>
                    <div class="footer-row"><div class="k">Conditions of Sales</div><div class="v">Customer accepts JPL's Conditions of Sale (included on the reverse side of each invoice and credit application).</div></div>
                    <div class="footer-row"><div class="k">Subject to Change</div><div class="v">Unless specifically addressed in writing, prices, terms and specifications are subject to change without notice.</div></div>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>

      </div>

      <!-- ✅ Clone Pricing Popup (Pablo modal styling) -->
      <div v-if="showCloneModal" class="popup-overlay" @click.self="closeCloneModal">
        <div class="popup-content" style="width:520px;">
          <div class="popup-header">
            <div style="font-family:'Cormorant Garamond', Garamond, serif; font-size:22px; font-weight:bold;">
              Clone Customer Pricing
            </div>
            <button class="close-btn" @click="closeCloneModal">×</button>
          </div>

          <div class="control" style="margin-top:10px; text-align:left;">
            <label>Source Customer #</label>
            <input v-model="cloneSourceCust" type="text" placeholder="ex: 12601" />
          </div>

          <div class="control" style="text-align:left;">
            <label>Target Customer #</label>
            <input v-model="cloneTargetCust" type="text" placeholder="ex: 54321" />
          </div>

          <div v-if="cloneError" class="panel error-box" style="margin-top:10px; text-align:left;">
            {{ cloneError }}
          </div>

          <div v-if="cloneSuccess" class="panel" style="margin-top:10px; text-align:left;">
            {{ cloneSuccess }}
          </div>

          <div style="margin-top:14px; display:flex; gap:10px; justify-content:flex-end; flex-wrap:wrap;">
            <button class="plan-it-btn" :disabled="cloneLoading" @click="submitClone(false)">
              {{ cloneLoading ? "Cloning..." : "Clone" }}
            </button>

            <button
              v-if="needsForceExpire"
              class="plan-it-btn"
              :disabled="cloneLoading"
              @click="submitClone(true)"
              title="Expires the target customer's active pricing and then clones."
            >
              Expire & Continue
            </button>

            <button class="plan-it-btn" :disabled="cloneLoading" @click="closeCloneModal">
              Close
            </button>
          </div>
        </div>
      </div>


      <!-- Update Pricing Modal -->
      <div v-if="showUpdatePricingModal" class="popup-overlay" @click.self="closeUpdatePricingModal">
        <div class="popup-content" style="width:560px;">
          <div class="popup-header">
            <div style="font-family:'Cormorant Garamond', Garamond, serif; font-size:22px; font-weight:bold;">
              Update Customer Pricing
            </div>
            <button class="close-btn" @click="closeUpdatePricingModal">×</button>
          </div>

          <div style="text-align:left; margin-top:8px; opacity:0.9;">
            Customer {{ sheetData?.cust_no }} — {{ childCustomerName }}
          </div>

          <div class="control" style="margin-top:10px; text-align:left;">
            <label>Percent Change</label>
            <input v-model="updatePct" type="text" placeholder="ex: 5 or -3.25" />
          </div>

          <div class="control" style="text-align:left;">
            <label>Effective Date</label>
            <input v-model="updateEffectiveDt" type="date" />
          </div>

          <div v-if="updatePricingError" class="panel error-box" style="margin-top:10px; text-align:left;">
            {{ updatePricingError }}
          </div>

          <div style="margin-top:12px; display:flex; gap:10px; justify-content:flex-end; flex-wrap:wrap;">
            <button class="plan-it-btn" :disabled="updatePreviewLoading" @click="previewUpdatePricing">
              {{ updatePreviewLoading ? "Previewing..." : "Preview" }}
            </button>

            <button class="plan-it-btn" :disabled="updateSubmitting || !updatePreview" @click="submitUpdatePricing">
              {{ updateSubmitting ? "Updating..." : "Confirm Update" }}
            </button>

            <button class="plan-it-btn" :disabled="updateSubmitting" @click="closeUpdatePricingModal">
              Close
            </button>
          </div>

          <!-- Preview section -->
          <div v-if="updatePreview" class="panel" style="margin-top:12px; text-align:left;">
            <div style="font-weight:bold; margin-bottom:6px;">Preview</div>
            <div>Active rows to update: <b>{{ updatePreview.active_count }}</b></div>
            <div v-if="updatePreview.future_count > 0" style="margin-top:6px; color:#b00020;">
              ⚠ Future effective pricing rows found: <b>{{ updatePreview.future_count }}</b>
            </div>

            <div style="margin-top:10px; font-weight:bold;">Sample (Tier 1)</div>
            <table class="popup-table" style="margin-top:6px;">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Prod Cat</th>
                  <th>Basis</th>
                  <th style="text-align:right;">Old</th>
                  <th style="text-align:right;">New</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in updatePreview.sample" :key="r.item_no + '-' + r.prod_cat">
                  <td>{{ r.item_no }}</td>
                  <td>{{ r.prod_cat }}</td>
                  <td>{{ r.cd_prc_basis }}</td>
                  <td style="text-align:right;">{{ formatMoney(r.old_prc_1) }}</td>
                  <td style="text-align:right;">{{ formatMoney(r.new_prc_1) }}</td>
                </tr>
                <tr v-if="!updatePreview.sample || updatePreview.sample.length === 0">
                  <td colspan="5" style="text-align:center; opacity:0.8; padding:10px;">No sample rows.</td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>
      </div>

      <!-- =========================================================
     MASS ITEM PRICE MODAL
      ========================================================= -->
      <div
        v-if="showMassItemPricingModal"
        class="popup-overlay"
        @click.self="closeMassItemPricingModal"
      >
        <div class="popup-content" style="width:850px;">
          <div class="popup-header">
            <div>
              <div
                style="
                  font-family:'Cormorant Garamond', Garamond, serif;
                  font-size:22px;
                  font-weight:bold;
                "
              >
                Mass Item Pricing
              </div>

              <div style="font-size:13px; opacity:0.85; margin-top:3px;">
                Set one SKU to a fixed price across a customer list or range.
              </div>
            </div>

            <button
              class="close-btn"
              @click="closeMassItemPricingModal"
            >
              ×
            </button>
          </div>

          <!-- Customer list -->
          <div
            class="control"
            style="margin-top:10px; text-align:left;"
          >
            <label>Customer Range / List</label>

            <textarea
              v-model="massItemCustomerRange"
              class="mass-price-textarea"
              rows="5"
              placeholder="Paste customer numbers, one per line, comma-separated, or ranges such as 11135-11161"
              @input="clearMassItemPricingPreview"
            ></textarea>
          </div>

          <div class="pricing-date-grid" style="margin-top:10px;">
            <!-- Item -->
            <div class="control">
              <label>Item #</label>
              <input
                v-model="massItemNo"
                type="text"
                placeholder="ex: MNI25-04"
                @input="clearMassItemPricingPreview"
              />
            </div>

            <!-- Price -->
            <div class="control">
              <label>New Price</label>
              <input
                v-model="massItemNewPrice"
                type="text"
                placeholder="ex: 9.04"
                @input="clearMassItemPricingPreview"
              />
            </div>

            <!-- Effective date -->
            <div class="control">
              <label>Effective Date</label>
              <input
                v-model="massItemEffectiveDt"
                type="date"
                @change="clearMassItemPricingPreview"
              />
            </div>
          </div>

          <div
            v-if="massItemPricingError"
            class="panel error-box"
            style="margin-top:10px;"
          >
            {{ massItemPricingError }}
          </div>

          <div
            v-if="massItemPricingSuccess"
            class="panel success-box"
            style="margin-top:10px;"
          >
            {{ massItemPricingSuccess }}
          </div>

          <!-- Actions -->
          <div
            style="
              margin-top:14px;
              display:flex;
              gap:10px;
              justify-content:flex-end;
              flex-wrap:wrap;
            "
          >
            <button
              class="plan-it-btn"
              :disabled="massItemPreviewLoading || massItemSubmitting"
              @click="previewMassItemPricing"
            >
              {{
                massItemPreviewLoading
                  ? "Previewing..."
                  : "Preview"
              }}
            </button>

            <button
              class="plan-it-btn danger-btn"
              :disabled="
                massItemSubmitting ||
                !massItemPricingPreview ||
                Number(massItemPricingPreview.blocking_count || 0) > 0 ||
                Number(massItemPricingPreview.ready_count || 0) <= 0
              "
              @click="submitMassItemPricing"
            >
              {{
                massItemSubmitting
                  ? "Updating..."
                  : "Confirm Update"
              }}
            </button>

            <button
              class="plan-it-btn"
              :disabled="massItemPreviewLoading || massItemSubmitting"
              @click="closeMassItemPricingModal"
            >
              Close
            </button>
          </div>

          <!-- Preview -->
          <div
            v-if="massItemPricingPreview"
            class="panel"
            style="margin-top:14px;"
          >
            <div style="font-weight:bold; margin-bottom:6px;">
              Item:
              {{ massItemPricingPreview.item_no }}
              |
              New Price:
              ${{ formatMoney(massItemPricingPreview.new_price) }}
            </div>

            <div style="margin-bottom:8px;">
              Customers:
              <b>{{ massItemPricingPreview.customer_count || 0 }}</b>

              &nbsp; | &nbsp;

              Ready:
              <b>{{ massItemPricingPreview.ready_count || 0 }}</b>

              &nbsp; | &nbsp;

              Blocking:
              <b>
                {{ massItemPricingPreview.blocking_count || 0 }}
              </b>

              &nbsp; | &nbsp;

              Effective:
              <b>{{ massItemPricingPreview.effective_dt }}</b>
            </div>

            <div
              v-if="Number(massItemPricingPreview.blocking_count || 0) > 0"
              class="panel error-box"
              style="margin-bottom:10px;"
            >
              One or more accounts cannot be updated safely.
              Resolve the rows shown below before confirming.
            </div>

            <div class="table-scroll">
              <table
                class="popup-table"
                style="min-width:760px;"
              >
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Status</th>
                    <th>Current Start</th>
                    <th style="text-align:right;">
                      Current
                    </th>
                    <th style="text-align:right;">
                      New
                    </th>
                    <th style="text-align:right;">
                      Active Rows
                    </th>
                    <th style="text-align:right;">
                      Future
                    </th>
                    <th>Message</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="r in massItemPricingPreview.rows"
                    :key="r.cust_no"
                  >
                    <td>{{ r.cust_no }}</td>

                    <td>
                      <b>{{ r.status }}</b>
                    </td>

                    <td>
                      {{
                        r.current_start_dt
                          ? formatDate(r.current_start_dt)
                          : "-"
                      }}
                    </td>

                    <td style="text-align:right;">
                      {{
                        r.current_price == null
                          ? "-"
                          : formatMoney(r.current_price)
                      }}
                    </td>

                    <td style="text-align:right;">
                      {{
                        r.new_price == null
                          ? "-"
                          : formatMoney(r.new_price)
                      }}
                    </td>

                    <td style="text-align:right;">
                      {{ r.active_count ?? "-" }}
                    </td>

                    <td style="text-align:right;">
                      {{ r.future_count ?? "-" }}
                    </td>

                    <td>
                      {{ r.message || "" }}
                    </td>
                  </tr>

                  <tr
                    v-if="
                      !massItemPricingPreview.rows ||
                      massItemPricingPreview.rows.length === 0
                    "
                  >
                    <td
                      colspan="8"
                      style="
                        text-align:center;
                        opacity:0.8;
                        padding:10px;
                      "
                    >
                      No rows returned.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Pricing Dates / Delete Modal -->
      <div v-if="showPricingDatesModal" class="popup-overlay" @click.self="closePricingDatesModal">
        <div class="popup-content" style="width:640px;">
          <div class="popup-header">
            <div style="font-family:'Cormorant Garamond', Garamond, serif; font-size:22px; font-weight:bold;">
              Pricing Dates / Delete Rows
            </div>
            <button class="close-btn" @click="closePricingDatesModal">×</button>
          </div>

          <div style="text-align:left; margin-top:8px; opacity:0.9;">
            Customer {{ sheetData?.cust_no }} — {{ childCustomerName }}
          </div>

          <!-- EDIT DATE SECTION -->
          <div class="panel" style="margin-top:12px;">
            <div style="font-weight:bold; margin-bottom:8px;">Edit Effective / Expiration Date</div>

            <div class="pricing-date-grid">
              <div class="control">
                <label>Item # Optional</label>
                <input v-model="pricingDatesItemNo" type="text" placeholder="blank = all matching rows" />
              </div>

              <div class="control">
                <label>Current Effective Date</label>
                <input v-model="pricingDatesCurrentEffectiveDt" type="date" />
              </div>

              <div class="control">
                <label>Current Expiration Date</label>
                <input v-model="pricingDatesCurrentExpirationDt" type="date" />
              </div>

              <div class="control">
                <label>New Effective Date</label>
                <input v-model="pricingDatesNewEffectiveDt" type="date" />
              </div>

              <div class="control">
                <label>New Expiration Date</label>
                <input v-model="pricingDatesNewExpirationDt" type="date" />
              </div>
            </div>

            <div v-if="pricingDatesError" class="panel error-box" style="margin-top:10px;">
              {{ pricingDatesError }}
            </div>

            <div v-if="pricingDatesSuccess" class="panel success-box" style="margin-top:10px;">
              {{ pricingDatesSuccess }}
            </div>

            <div v-if="debugPricingDatesError" class="panel error-box" style="margin-top:10px;">
              {{ debugPricingDatesError }}
            </div>

            <div v-if="debugPricingDatesRows && debugPricingDatesRows.length" class="panel" style="margin-top:10px;">
              <div style="font-weight:bold; margin-bottom:6px;">
                Debug Rows Found: {{ debugPricingDatesRows.length }}
                <span v-if="debugPricingDatesDb"> | DB: {{ debugPricingDatesDb }}</span>
              </div>

              <table class="popup-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>cd_tp</th>
                    <th>curr</th>
                    <th>Cust</th>
                    <th>Item</th>
                    <th>Start</th>
                    <th>End</th>
                    <th style="text-align:right;">Price 1</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in debugPricingDatesRows" :key="r.ID">
                    <td>{{ r.ID }}</td>
                    <td>{{ r.cd_tp }}</td>
                    <td>{{ r.curr_cd }}</td>
                    <td>{{ r.cust_no }}</td>
                    <td>{{ r.item_no }}</td>
                    <td>{{ formatDate(r.start_dt) }}</td>
                    <td>{{ formatDate(r.end_dt) }}</td>
                    <td style="text-align:right;">{{ formatMoney(r.prc_or_disc_1) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style="margin-top:12px; display:flex; justify-content:flex-end;">

              <button
                class="plan-it-btn"
                :disabled="debugPricingDatesLoading"
                @click="debugCustomerPricingDates"
              >
                {{ debugPricingDatesLoading ? "Checking..." : "Debug Dates" }}
              </button>

              <button class="plan-it-btn" :disabled="pricingDatesLoading" @click="submitPricingDateEdit">
                {{ pricingDatesLoading ? "Saving..." : "Save Dates" }}
              </button>
            </div>
          </div>

          <!-- MASS DATE CHANGE SECTION -->
          <div class="panel" style="margin-top:12px;">
            <div style="font-weight:bold; margin-bottom:8px;">Mass Date Change</div>

            <div class="control">
              <label>Customer Range / List</label>
              <input
                v-model="massCustomerRange"
                type="text"
                placeholder="ex: 11135-11161,12597,12975,13065-13068"
              />
            </div>

            <div class="pricing-date-grid" style="margin-top:10px;">
              <div class="control">
                <label>Current Effective Date</label>
                <input v-model="massCurrentEffectiveDt" type="date" />
              </div>

              <div class="control">
                <label>Current Expiration Date</label>
                <input v-model="massCurrentExpirationDt" type="date" />
              </div>

              <div class="control">
                <label>New Effective Date</label>
                <input v-model="massNewEffectiveDt" type="date" />
              </div>

              <div class="control">
                <label>New Expiration Date</label>
                <input v-model="massNewExpirationDt" type="date" />
              </div>
            </div>

            <label class="checkbox-row">
              <input v-model="massDryRun" type="checkbox" />
              Dry run first
            </label>

            <div v-if="massPricingError" class="panel error-box" style="margin-top:10px;">
              {{ massPricingError }}
            </div>

            <div v-if="massPricingSuccess" class="panel success-box" style="margin-top:10px;">
              {{ massPricingSuccess }}
            </div>

            <div style="margin-top:12px; display:flex; gap:10px; justify-content:flex-end; flex-wrap:wrap;">
              <button class="plan-it-btn" :disabled="massPricingLoading" @click="submitMassPricingDateChange">
                {{ massPricingLoading ? "Running..." : (massDryRun ? "Preview Mass Change" : "Run Mass Change") }}
              </button>

              <button
                v-if="massPricingPreview && massDryRun"
                class="plan-it-btn danger-btn"
                :disabled="massPricingLoading"
                @click="confirmMassPricingDateChange"
              >
                Confirm Mass Change
              </button>
            </div>

            <div v-if="massPricingPreview" class="panel" style="margin-top:12px;">
              <div style="font-weight:bold; margin-bottom:6px;">
                Customers: {{ massPricingPreview.customer_count }}
                |
                Matching Rows: {{ massPricingPreview.total_matching_rows || massPricingPreview.total_updated_rows || 0 }}
                <span v-if="massPricingPreview.db"> | DB: {{ massPricingPreview.db }}</span>
              </div>

              <table class="popup-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th style="text-align:right;">Matching / Updated Rows</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in massPricingPreview.rows" :key="r.cust_no">
                    <td>{{ r.cust_no }}</td>
                    <td style="text-align:right;">
                      {{ r.matching_rows != null ? r.matching_rows : r.updated_rows }}
                    </td>
                  </tr>

                  <tr v-if="!massPricingPreview.rows || massPricingPreview.rows.length === 0">
                    <td colspan="2" style="text-align:center; opacity:0.8; padding:10px;">
                      No rows returned.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- DELETE SECTION -->
          <div class="panel" style="margin-top:12px;">
            <div style="font-weight:bold; margin-bottom:8px;">Delete Pricing Rows by Date</div>

            <div class="pricing-date-grid">
              <div class="control">
                <label>Item # Optional</label>
                <input v-model="deletePricingItemNo" type="text" placeholder="blank = all matching rows" />
              </div>

              <div class="control">
                <label>Effective Date Optional</label>
                <input v-model="deletePricingEffectiveDt" type="date" />
              </div>

              <div class="control">
                <label>Expiration Date Optional</label>
                <input v-model="deletePricingExpirationDt" type="date" />
              </div>
            </div>

            <label class="checkbox-row">
              <input v-model="deletePricingDryRun" type="checkbox" />
              Dry run first
            </label>

            <div v-if="deletePricingError" class="panel error-box" style="margin-top:10px;">
              {{ deletePricingError }}
            </div>

            <div v-if="deletePricingSuccess" class="panel success-box" style="margin-top:10px;">
              {{ deletePricingSuccess }}
            </div>

            <div style="margin-top:12px; display:flex; gap:10px; justify-content:flex-end; flex-wrap:wrap;">
              <button class="plan-it-btn" :disabled="deletePricingLoading" @click="submitDeletePricing">
                {{ deletePricingLoading ? "Checking..." : (deletePricingDryRun ? "Preview Delete" : "Delete Rows") }}
              </button>

              <button
                v-if="deletePricingPreview && deletePricingDryRun"
                class="plan-it-btn danger-btn"
                :disabled="deletePricingLoading"
                @click="confirmDeletePricing"
              >
                Confirm Delete
              </button>
            </div>

            <div v-if="deletePricingPreview" class="panel" style="margin-top:12px;">
              <div style="font-weight:bold; margin-bottom:6px;">
                Matching rows: {{ deletePricingPreview.matching_rows || deletePricingPreview.deleted_rows || 0 }}
              </div>

              <table v-if="deletePricingPreview.sample && deletePricingPreview.sample.length" class="popup-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Start</th>
                    <th>End</th>
                    <th style="text-align:right;">Qty 1</th>
                    <th style="text-align:right;">Price 1</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in deletePricingPreview.sample" :key="r.ID">
                    <td>{{ r.item_no }}</td>
                    <td>{{ formatDate(r.start_dt) }}</td>
                    <td>{{ formatDate(r.end_dt) }}</td>
                    <td style="text-align:right;">{{ formatNumber(r.minimum_qty_1) }}</td>
                    <td style="text-align:right;">{{ formatMoney(r.prc_or_disc_1) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div style="margin-top:14px; display:flex; justify-content:flex-end;">
            <button class="plan-it-btn" @click="closePricingDatesModal">
              Close
            </button>
          </div>
        </div>
      </div>


      <!-- ✅ Pablo Popup Modal (Item YTD / Open Orders / Sales History) -->
<div v-if="showPopup" class="popup-overlay" @click.self="closePopup">
  <div class="popup-content">

    <div class="popup-header">
      <div style="text-align:left;">
        <div style="font-weight:bold; font-size:16px;">
          {{ popupTitle }}
        </div>
        <div style="font-size:13px; opacity:0.85;">
          Customer {{ sheetData?.cust_no }} — {{ childCustomerName }}
          <span v-if="selectedItemNo"> • Item: {{ selectedItemNo }}</span>
        </div>
      </div>

      <button class="close-btn" @click="closePopup">✕</button>
    </div>

    <!-- ✅ Tab buttons -->
    <div class="popup-tabs">
      <button
        class="popup-tab-btn"
        :class="{ active: activeTab === 'item' }"
        @click="switchTab('item')"
        :disabled="!selectedItemNo"
        title="Click a price cell to enable"
      >
        Item YTD
      </button>

      <button
        class="popup-tab-btn"
        :class="{ active: activeTab === 'open' }"
        @click="switchTab('open')"
      >
        Open Orders
      </button>

      <button
        class="popup-tab-btn"
        :class="{ active: activeTab === 'history' }"
        @click="switchTab('history')"
      >
        Sales History
      </button>

      <button
        class="popup-tab-btn"
        :class="{ active: activeTab === 'summary' }"
        @click="openSalesSummaryModal"
      >
        Sales Summary
      </button>

    </div>

    <!-- ✅ Loading / Error inside modal -->
    <div v-if="popupLoading" style="margin-top:10px; font-weight:bold;">
      Loading...
    </div>

    <div v-if="popupError" class="panel error-box" style="margin-top:10px;">
      {{ popupError }}
    </div>

    <!-- ✅ TAB: ITEM YTD -->
    <div v-if="activeTab === 'item' && !popupLoading && !popupError" style="margin-top:10px;">
      <div v-if="!selectedItemNo" style="opacity:0.85;">
        Click any price in the matrix to view Item YTD.
      </div>

      <div v-else class="popup-metrics">
        <div class="metric">
          <div class="k">Qty Shipped (YTD)</div>
          <div class="v">{{ formatNumber(itemYtd?.qty_shipped) }}</div>
        </div>
        <div class="metric">
          <div class="k">Net Sales (YTD)</div>
          <div class="v">{{ formatMoney(itemYtd?.net_sales) }}</div>
        </div>
        <div class="metric">
          <div class="k">Avg Sell Price</div>
          <div class="v">{{ formatMoney(itemYtd?.avg_sell_price) }}</div>
        </div>
        <div class="metric">
          <div class="k">Last Activity</div>
          <div class="v">{{ formatDate(itemYtd?.last_activity_dt) }}</div>
        </div>
      </div>
    </div>

    <!-- ✅ TAB: OPEN ORDERS -->
    <div v-if="activeTab === 'open' && !popupLoading && !popupError" style="margin-top:10px;">
      <div class="popup-subheader">
        <span>
          Showing open FG lines (ship quantity &gt; 0)
          <span v-if="openOrdersItemFilter"> • Filtered to item {{ openOrdersItemFilter }}</span>
        </span>

        <button class="small-btn" @click="toggleOpenOrdersItemFilter" :disabled="!selectedItemNo">
          {{ openOrdersItemFilter ? "Show All Items" : "Filter to Clicked Item" }}
        </button>
      </div>

      <table class="popup-table">
        <thead>
          <tr>
            <th>Req Ship</th>
            <th>Order</th>
            <th>Item</th>
            <th>Description</th>
            <th>Loc</th>
            <th style="text-align:right;">Qty To Ship</th>
            <th style="text-align:right;">Unit Price</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in openOrdersRows" :key="r.ord_no + '-' + r.item_no + '-' + r.req_ship_dt">
            <td>{{ formatDate(r.req_ship_dt) }}</td>
            <td>{{ r.ord_no }}</td>
            <td>{{ r.item_no }}</td>
            <td>{{ r.item_desc_1 }}</td>
            <td>{{ r.loc }}</td>
            <td style="text-align:right;">{{ formatNumber(r.qty_to_ship) }}</td>
            <td style="text-align:right;">{{ formatMoney(r.unit_price) }}</td>
          </tr>

          <tr v-if="!openOrdersRows || openOrdersRows.length === 0">
            <td colspan="7" style="text-align:center; opacity:0.8; padding:10px;">
              No open lines found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ✅ TAB: SALES HISTORY -->
    <div v-if="activeTab === 'history' && !popupLoading && !popupError" style="margin-top:10px;">
      <div class="popup-subheader">
        <div class="history-controls">
          <div class="control" style="max-width:170px;">
            <label>Start</label>
            <input v-model="historyStart" type="date" />
          </div>

          <div class="control" style="max-width:170px;">
            <label>End</label>
            <input v-model="historyEnd" type="date" />
          </div>

          <button class="small-btn" @click="loadSalesHistory()">
            Refresh
          </button>

          <button class="small-btn" @click="toggleHistoryItemFilter" :disabled="!selectedItemNo">
            {{ historyItemFilter ? "All Items" : "Clicked Item Only" }}
          </button>
        </div>

        <div style="margin-top:6px; opacity:0.85;">
          Rows: {{ salesHistoryRows.length }} / {{ salesHistoryTotal }}
          <span v-if="historyItemFilter"> • Filtered to item {{ historyItemFilter }}</span>
        </div>

        <div class="history-paging">
          <button class="small-btn" @click="prevHistoryPage" :disabled="historyOffset === 0">
            Prev
          </button>
          <button
            class="small-btn"
            @click="nextHistoryPage"
            :disabled="historyOffset + historyLimit >= salesHistoryTotal"
          >
            Next
          </button>
        </div>
      </div>

      <table class="popup-table">
        <thead>
          <tr>
            <th>Activity</th>
            <th>Invoice</th>
            <th>Order</th>
            <th>Item</th>
            <th>Description</th>
            <th style="text-align:right;">Qty Shipped</th>
            <th style="text-align:right;">Sales</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in salesHistoryRows" :key="r.inv_no + '-' + r.item_no + '-' + r.activity_dt">
            <td>{{ formatDate(r.activity_dt) }}</td>
            <td>{{ r.inv_no }}</td>
            <td>{{ r.ord_no }}</td>
            <td>{{ r.item_no }}</td>
            <td>{{ r.item_desc_1 }}</td>
            <td style="text-align:right;">{{ formatNumber(r.qty_shipped) }}</td>
            <td style="text-align:right;">{{ formatMoney(r.sls_amt) }}</td>
          </tr>

          <tr v-if="!salesHistoryRows || salesHistoryRows.length === 0">
            <td colspan="7" style="text-align:center; opacity:0.8; padding:10px;">
              No history rows found for this range.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="activeTab === 'summary'" class="tab-panel">

    <div class="pablo-summary-bar">
      <div class="pablo-summary-left">

        <!-- Quick ranges -->
        <div class="pablo-chip-row">
          <button class="pablo-chip" @click="setSummaryRange('ytd')">YTD</button>
          <button class="pablo-chip" @click="setSummaryRange('30')">Last 30</button>
          <button class="pablo-chip" @click="setSummaryRange('90')">Last 90</button>
          <button class="pablo-chip" @click="setSummaryRange('365')">Last 365</button>

          <!-- little status pill -->
          <span class="pablo-pill" :class="{ ok: summaryDatesValid, bad: !summaryDatesValid }">
            {{ summaryDatesValid ? "Ready" : "Pick valid dates" }}
          </span>
        </div>

        <!-- Date inputs -->
        <div class="pablo-date-row">
          <div class="control" style="max-width:170px;">
            <label>Start</label>
            <input
              type="date"
              v-model="historyStart"
              @keyup.enter="runSalesSummary"
            />
          </div>

          <div class="control" style="max-width:170px;">
            <label>End</label>
            <input
              type="date"
              v-model="historyEnd"
              @keyup.enter="runSalesSummary"
            />
          </div>

          <div v-if="summaryDateError" class="pablo-inline-error">
            {{ summaryDateError }}
          </div>
        </div>

      </div>

      <!-- GO -->
      <div class="pablo-summary-right">
        <button
          class="plan-it-btn pablo-go-btn"
          @click="runSalesSummary"
          :disabled="summaryLoading || !summaryDatesValid"
          title="Run summary for selected dates"
        >
          {{ summaryLoading ? "Running..." : "GO" }}
        </button>
      </div>
    </div>


        <div v-if="summaryLoading" class="loading-line">Loading summary…</div>

          <div v-else>
            <div class="summary-totals-bar">
              <div class="tot">
                <div class="k">Curr $</div>
                <div class="v">{{ formatMoney(salesSummaryTotals.dollars_curr) }}</div>
              </div>
              <div class="tot">
                <div class="k">Curr Feet</div>
                <div class="v">{{ formatNumber(salesSummaryTotals.feet_curr) }}</div>
              </div>
              <div class="tot">
                <div class="k">Prev $</div>
                <div class="v">{{ formatMoney(salesSummaryTotals.dollars_prev) }}</div>
              </div>
              <div class="tot">
                <div class="k">Prev Feet</div>
                <div class="v">{{ formatNumber(salesSummaryTotals.feet_prev) }}</div>
              </div>

              <div class="tot">
                <div class="k">Δ $</div>
                <div class="v">
                  {{ salesSummaryTotals.pctDollarChg == null ? "-" : formatPct(salesSummaryTotals.pctDollarChg) }}
                </div>
              </div>

              <div class="tot">
                <div class="k">Δ Feet</div>
                <div class="v">
                  {{ salesSummaryTotals.pctFeetChg == null ? "-" : formatPct(salesSummaryTotals.pctFeetChg) }}
                </div>
              </div>
            </div>

            <table class="popup-table">
              <thead>
                <tr>
                  <th @click="setSummarySort('item_no')" style="cursor:pointer;">Item</th>
                  <th @click="setSummarySort('item_desc_1')" style="cursor:pointer;">Description</th>

                  <th @click="setSummarySort('dollars_curr')" style="cursor:pointer;">Curr $</th>
                  <th @click="setSummarySort('feet_curr')" style="cursor:pointer;">Curr Feet</th>
                  <th @click="setSummarySort('pct_dollars_curr')" style="cursor:pointer;">Curr % $</th>
                  <th @click="setSummarySort('pct_feet_curr')" style="cursor:pointer;">Curr % Feet</th>

                  <th @click="setSummarySort('dollars_prev')" style="cursor:pointer;">Prev $</th>
                  <th @click="setSummarySort('feet_prev')" style="cursor:pointer;">Prev Feet</th>
                  <th @click="setSummarySort('pct_dollars_prev')" style="cursor:pointer;">Prev % $</th>
                  <th @click="setSummarySort('pct_feet_prev')" style="cursor:pointer;">Prev % Feet</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="r in sortedSalesSummaryRows" :key="r.item_no">
                  <td>{{ r.item_no }}</td>
                  <td>{{ r.item_desc_1 }}</td>

                  <td>{{ formatMoney(r.dollars_curr) }}</td>
                  <td>{{ formatNumber(r.feet_curr) }}</td>
                  <td>{{ formatPct(r.pct_dollars_curr) }}</td>
                  <td>{{ formatPct(r.pct_feet_curr) }}</td>

                  <td>{{ formatMoney(r.dollars_prev) }}</td>
                  <td>{{ formatNumber(r.feet_prev) }}</td>
                  <td>{{ formatPct(r.pct_dollars_prev) }}</td>
                  <td>{{ formatPct(r.pct_feet_prev) }}</td>
                </tr>

                <tr v-if="!sortedSalesSummaryRows || sortedSalesSummaryRows.length === 0">
                  <td colspan="10" style="text-align:center; opacity:0.8; padding:10px;">
                    No summary rows found.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

    </div>


  </div>
</div>


    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "PriceSheetsPage",

  data() {
    return {
      custNo: "",
      loading: false,
      error: null,

      sheetData: null,

      // ✅ Popup modal state
      showPopup: false,
      popupLoading: false,
      popupError: null,
      activeTab: "item",
      popupTitle: "Customer Insights",

      selectedItemNo: null,          // item from clicked price cell

      // Tab data
      itemYtd: null,

      openOrdersRows: [],
      openOrdersItemFilter: null,    // null = all items, or item_no string

      salesHistoryRows: [],
      salesHistoryTotal: 0,
      historyStart: "",              // YYYY-MM-DD
      historyEnd: "",                // YYYY-MM-DD
      historyLimit: 200,
      historyOffset: 0,
      historyItemFilter: null,        // null = all items, or item_no string


      showCloneModal: false,
      cloneSourceCust: "",
      cloneTargetCust: "",
      cloneLoading: false,
      cloneError: null,
      cloneSuccess: null,
      needsForceExpire: false,

      salesSummaryRows: [],
      summaryLoading: false,
      salesSummaryStart: null,
      salesSummaryEnd: null,
      summaryDateError: null,
      summarySortKey: "feet_curr",
      summarySortDir: "desc", // "asc" | "desc"

      showUpdatePricingModal: false,
      updatePct: "",
      updateEffectiveDt: "",
      updatePreview: null,
      updatePreviewLoading: false,
      updateSubmitting: false,
      updatePricingError: null,

      showPricingDatesModal: false,

      pricingDatesItemNo: "",
      pricingDatesCurrentEffectiveDt: "",
      pricingDatesCurrentExpirationDt: "",
      pricingDatesNewEffectiveDt: "",
      pricingDatesNewExpirationDt: "",

      pricingDatesLoading: false,
      pricingDatesError: null,
      pricingDatesSuccess: null,

      deletePricingItemNo: "",
      deletePricingEffectiveDt: "",
      deletePricingExpirationDt: "",
      deletePricingDryRun: true,
      deletePricingLoading: false,
      deletePricingError: null,
      deletePricingSuccess: null,
      deletePricingPreview: null,

      debugPricingDatesLoading: false,
      debugPricingDatesError: null,
      debugPricingDatesRows: [],
      debugPricingDatesDb: null,

      massCustomerRange: "",
      massCurrentEffectiveDt: "",
      massCurrentExpirationDt: "",
      massNewEffectiveDt: "",
      massNewExpirationDt: "",
      massDryRun: true,
      massPricingLoading: false,
      massPricingError: null,
      massPricingSuccess: null,
      massPricingPreview: null,

      // Mass fixed item price
      showMassItemPricingModal: false,
      massItemCustomerRange: "",
      massItemNo: "",
      massItemNewPrice: "",
      massItemEffectiveDt: "",

      massItemPreviewLoading: false,
      massItemSubmitting: false,
      massItemPricingError: null,
      massItemPricingSuccess: null,
      massItemPricingPreview: null,

      printNameMode: "parent", // "parent" or "child"


    };
  },

  computed: {
    priceSheetLoaded() {
      return !!this.sheetData;
    },

    customerName() {
      return this.sheetData?.customer?.parent_name
        || this.sheetData?.customer?.InvoiceName
        || this.sheetData?.customer?.cmp_name
        || "Unknown Customer";
    },

    customerCityState() {
      const city = this.sheetData?.customer?.city || "";
      const state = this.sheetData?.customer?.state_code || "";

      if (city && state) return `${city}, ${state}`;
      if (city) return city;
      if (state) return state;
      return "";
    },

    childCustomerName() {
      return this.sheetData?.customer?.cmp_name?.trim?.()
        || this.sheetData?.customer?.child_name?.trim?.()
        || "";
    },

    notesText() {
      const notes = this.sheetData?.notes?.PriceSheetNotes;
      return notes && notes.trim().length > 0 ? notes : "(No notes found)";
    },
    freightMultipliers() {
        const n = this.sheetData?.notes || {};
        return {
            fullTL: n.FreeNumberField_01 ?? "-",
            x20000: n.FreeNumberField_02 ?? "-",
            x12000: n.FreeNumberField_03 ?? "-",
            x8000: n.FreeNumberField_04 ?? "-"
            
        };
    },

    summaryDatesValid() {
      return (
        this.isValidYyyyMmDd(this.historyStart) &&
        this.isValidYyyyMmDd(this.historyEnd) &&
        this.historyStart <= this.historyEnd
      );
    },

    salesSummaryTotals() {
      const rows = this.salesSummaryRows || [];

      const sum = (arr, key) => arr.reduce((a, r) => a + Number(r?.[key] || 0), 0);

      const dollars_curr = sum(rows, "dollars_curr");
      const feet_curr    = sum(rows, "feet_curr");
      const dollars_prev = sum(rows, "dollars_prev");
      const feet_prev    = sum(rows, "feet_prev");

      const pctDollarChg = dollars_prev ? (dollars_curr - dollars_prev) / dollars_prev : null;
      const pctFeetChg   = feet_prev ? (feet_curr - feet_prev) / feet_prev : null;

      return {
        dollars_curr, feet_curr,
        dollars_prev, feet_prev,
        pctDollarChg, pctFeetChg
      };
    },

    sortedSalesSummaryRows() {
      const rows = [...(this.salesSummaryRows || [])];
      const key = this.summarySortKey;
      const dir = this.summarySortDir;

      const isNum = (v) => typeof v === "number" || (v != null && v !== "" && !isNaN(Number(v)));

      rows.sort((a, b) => {
        const av = a?.[key];
        const bv = b?.[key];

        // numeric sort when possible
        if (isNum(av) && isNum(bv)) {
          const na = Number(av || 0);
          const nb = Number(bv || 0);
          return dir === "asc" ? na - nb : nb - na;
        }

        // string sort fallback
        const sa = String(av ?? "").toLowerCase();
        const sb = String(bv ?? "").toLowerCase();
        if (sa < sb) return dir === "asc" ? -1 : 1;
        if (sa > sb) return dir === "asc" ? 1 : -1;
        return 0;
      });

      return rows;
    },



    freightMultipliersExist() {
        if (!this.sheetData?.notes) return false;
        const n = this.sheetData.notes;
        return (
            n.FreeNumberField_01 != null ||
            n.FreeNumberField_02 != null ||
            n.FreeNumberField_03 != null ||
            n.FreeNumberField_04 != null 
            
        );
    }

  },

  methods: {
    async loadPriceSheet() {
      this.error = null;
      this.sheetData = null;

      const custNoTrim = String(this.custNo || "").trim();
      if (!custNoTrim) return;

      // Basic numeric validation (backend will enforce too)
      if (!/^[0-9]+$/.test(custNoTrim)) {
        this.error = "Customer number must be numeric.";
        return;
      }

      this.loading = true;

      try {
        const { data } = await axios.get("/getPriceSheetMatrix", {
          params: { cust_no: custNoTrim }
        });

        this.sheetData = data;
      } catch (err) {
        console.error("loadPriceSheet error:", err);
        this.error =
          err?.response?.data?.error ||
          err?.response?.data?.details ||
          "Failed to load price sheet.";
      } finally {
        this.loading = false;
      }
    },

    formatPrice(v) {
      const num = Number(v);
      if (!isFinite(num)) return "";
      return num.toFixed(2);
    },

    openPrintView() {
      if (!this.custNo) return;

      const mode = this.printNameMode || "parent";

      window.open(
        `/print-price-sheet?cust_no=${this.custNo}&name_mode=${mode}`,
        "_blank"
      );
    },

    openCloneModal() {
      this.cloneError = null;
      this.cloneSuccess = null;
      this.needsForceExpire = false;

      // Pre-fill source with loaded sheet customer if present, else whatever is typed
      const current = String(this.sheetData?.cust_no || this.custNo || "").trim();
      this.cloneSourceCust = current;
      this.cloneTargetCust = "";

      this.showCloneModal = true;
    },

    closeCloneModal() {
      this.showCloneModal = false;
      this.cloneLoading = false;
      this.needsForceExpire = false;
    },

    setSummarySort(key) {
      if (this.summarySortKey === key) {
        this.summarySortDir = this.summarySortDir === "desc" ? "asc" : "desc";
      } else {
        this.summarySortKey = key;
        this.summarySortDir = "desc"; // default to desc when switching columns
      }
    },

    async debugCustomerPricingDates() {
      this.debugPricingDatesError = null;
      this.debugPricingDatesRows = [];
      this.debugPricingDatesDb = null;

      if (!this.priceSheetLoaded) return;

      this.debugPricingDatesLoading = true;

      try {
        const { data } = await axios.get("/debugCustomerPricingDates", {
          params: {
            cust_no: String(this.sheetData.cust_no).trim()
          }
        });

        this.debugPricingDatesDb = data?.db || null;
        this.debugPricingDatesRows = data?.rows || [];

        if (!this.debugPricingDatesRows.length) {
          this.debugPricingDatesError =
            `Route worked, but no pricing rows were found for customer ${this.sheetData.cust_no}. Check PRICE_DB.`;
        }

      } catch (err) {
        const payload = err?.response?.data;
        this.debugPricingDatesError =
          payload?.error ||
          payload?.details ||
          "Failed to debug customer pricing dates.";
      } finally {
        this.debugPricingDatesLoading = false;
      }
    },

    openPricingDatesModal() {
      this.pricingDatesError = null;
      this.pricingDatesSuccess = null;
      this.deletePricingError = null;
      this.deletePricingSuccess = null;
      this.deletePricingPreview = null;

      this.pricingDatesItemNo = "";
      this.pricingDatesCurrentEffectiveDt = "";
      this.pricingDatesCurrentExpirationDt = "";
      this.pricingDatesNewEffectiveDt = "";
      this.pricingDatesNewExpirationDt = "";

      this.deletePricingItemNo = "";
      this.deletePricingEffectiveDt = "";
      this.deletePricingExpirationDt = "";
      this.deletePricingDryRun = true;

      this.showPricingDatesModal = true;

      this.debugPricingDatesError = null;
      this.debugPricingDatesRows = [];
      this.debugPricingDatesDb = null;

      this.massCustomerRange = "";
      this.massCurrentEffectiveDt = "";
      this.massCurrentExpirationDt = "";
      this.massNewEffectiveDt = "";
      this.massNewExpirationDt = "";
      this.massDryRun = true;
      this.massPricingLoading = false;
      this.massPricingError = null;
      this.massPricingSuccess = null;
      this.massPricingPreview = null;
    },

    closePricingDatesModal() {
      this.showPricingDatesModal = false;
      this.pricingDatesLoading = false;
      this.deletePricingLoading = false;
      this.debugPricingDatesLoading = false;
    },

    async submitPricingDateEdit() {
      this.pricingDatesError = null;
      this.pricingDatesSuccess = null;

      if (!this.priceSheetLoaded) return;

      if (!this.pricingDatesCurrentEffectiveDt) {
        this.pricingDatesError = "Current effective date is required.";
        return;
      }

      if (!this.pricingDatesCurrentExpirationDt) {
        this.pricingDatesError = "Current expiration date is required.";
        return;
      }

      if (!this.pricingDatesNewEffectiveDt && !this.pricingDatesNewExpirationDt) {
        this.pricingDatesError = "Enter a new effective date, new expiration date, or both.";
        return;
      }

      this.pricingDatesLoading = true;

      try {
        const payload = {
          cust_no: String(this.sheetData.cust_no).trim(),
          current_effective_dt: this.pricingDatesCurrentEffectiveDt,
          current_expiration_dt: this.pricingDatesCurrentExpirationDt,
          new_effective_dt: this.pricingDatesNewEffectiveDt || null,
          new_expiration_dt: this.pricingDatesNewExpirationDt || null
        };

        const item = String(this.pricingDatesItemNo || "").trim();

        if (item) {
          payload.item_no = item;
        }
        const { data } = await axios.post("/editCustomerPricingDates", payload);

        this.pricingDatesSuccess =
          `Updated ${data.updated_rows || 0} pricing row(s).`;

        await this.loadPriceSheet();

      } catch (err) {
        const payload = err?.response?.data;
        this.pricingDatesError =
          payload?.error ||
          payload?.details ||
          "Failed to edit pricing dates.";
      } finally {
        this.pricingDatesLoading = false;
      }
    },

    async submitMassPricingDateChange() {
      this.massPricingError = null;
      this.massPricingSuccess = null;
      this.massPricingPreview = null;

      const range = String(this.massCustomerRange || "").trim();

      if (!range) {
        this.massPricingError = "Customer range/list is required.";
        return;
      }

      if (!this.massCurrentEffectiveDt) {
        this.massPricingError = "Current effective date is required.";
        return;
      }

      if (!this.massCurrentExpirationDt) {
        this.massPricingError = "Current expiration date is required.";
        return;
      }

      if (!this.massNewEffectiveDt && !this.massNewExpirationDt) {
        this.massPricingError = "Enter a new effective date, new expiration date, or both.";
        return;
      }

      this.massPricingLoading = true;

      try {
        const payload = {
          customer_range: range,
          current_effective_dt: this.massCurrentEffectiveDt,
          current_expiration_dt: this.massCurrentExpirationDt,
          new_effective_dt: this.massNewEffectiveDt || null,
          new_expiration_dt: this.massNewExpirationDt || null,
          dry_run: !!this.massDryRun
        };

        const { data } = await axios.post("/massEditCustomerPricingDates", payload);

        this.massPricingPreview = data;

        if (this.massDryRun) {
          this.massPricingSuccess =
            `Preview found ${data.total_matching_rows || 0} matching row(s) across ${data.customer_count || 0} customer(s). Nothing changed yet.`;
        } else {
          this.massPricingSuccess =
            `Updated ${data.total_updated_rows || 0} row(s) across ${data.customer_count || 0} customer(s).`;

          await this.loadPriceSheet();
        }

      } catch (err) {
        const payload = err?.response?.data;
        this.massPricingError =
          payload?.error ||
          payload?.details ||
          "Failed to run mass pricing date change.";
      } finally {
        this.massPricingLoading = false;
      }
    },

    async confirmMassPricingDateChange() {
      this.massDryRun = false;
      await this.submitMassPricingDateChange();
      this.massDryRun = true;
    },

    openMassItemPricingModal() {
      this.massItemPricingError = null;
      this.massItemPricingSuccess = null;
      this.massItemPricingPreview = null;

      // If a customer is currently loaded, prefill it.
      // Otherwise leave the list blank.
      this.massItemCustomerRange =
        String(
          this.sheetData?.cust_no ||
          this.custNo ||
          ""
        ).trim();

      this.massItemNo = "";
      this.massItemNewPrice = "";

      // Default effective date = local today.
      const now = new Date();

      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");

      this.massItemEffectiveDt =
        `${yyyy}-${mm}-${dd}`;

      this.massItemPreviewLoading = false;
      this.massItemSubmitting = false;

      this.showMassItemPricingModal = true;
    },

    closeMassItemPricingModal() {
      if (
        this.massItemPreviewLoading ||
        this.massItemSubmitting
      ) {
        return;
      }

      this.showMassItemPricingModal = false;
    },

    clearMassItemPricingPreview() {
      // Changing an input invalidates the previous preview.
      this.massItemPricingPreview = null;
      this.massItemPricingSuccess = null;
      this.massItemPricingError = null;
    },

    validateMassItemPricingInputs() {
      const range =
        String(
          this.massItemCustomerRange || ""
        ).trim();

      const item =
        String(
          this.massItemNo || ""
        ).trim();

      const price =
        Number(
          String(
            this.massItemNewPrice || ""
          ).trim()
        );

      if (!range) {
        this.massItemPricingError =
          "Customer range/list is required.";
        return false;
      }

      if (!item) {
        this.massItemPricingError =
          "Item number is required.";
        return false;
      }

      if (!isFinite(price) || price <= 0) {
        this.massItemPricingError =
          "New price must be a number greater than zero.";
        return false;
      }

      if (
        !this.isValidYyyyMmDd(
          this.massItemEffectiveDt
        )
      ) {
        this.massItemPricingError =
          "Effective date is required.";
        return false;
      }

      return true;
    },

    async previewMassItemPricing() {
      this.massItemPricingError = null;
      this.massItemPricingSuccess = null;
      this.massItemPricingPreview = null;

      if (!this.validateMassItemPricingInputs()) {
        return;
      }

      this.massItemPreviewLoading = true;

      try {
        const { data } = await axios.post(
          "/massEditCustomerItemPricing",
          {
            customer_range:
              String(
                this.massItemCustomerRange
              ).trim(),

            item_no:
              String(
                this.massItemNo
              ).trim(),

            new_price:
              Number(
                this.massItemNewPrice
              ),

            effective_dt:
              this.massItemEffectiveDt,

            dry_run: true
          }
        );

        this.massItemPricingPreview = data;

        const blocking =
          Number(
            data.blocking_count || 0
          );

        if (blocking > 0) {
          this.massItemPricingSuccess =
            `Preview completed. ` +
            `${data.ready_count || 0} customer(s) ready; ` +
            `${blocking} customer(s) require attention.`;
        } else {
          this.massItemPricingSuccess =
            `Preview completed. ` +
            `All ${data.ready_count || 0} customer(s) are ready. ` +
            `Nothing changed yet.`;
        }

      } catch (err) {
        const payload =
          err?.response?.data;

        this.massItemPricingError =
          payload?.error ||
          payload?.details ||
          "Failed to preview mass item pricing.";

        if (payload?.rows) {
          this.massItemPricingPreview = payload;
        }

      } finally {
        this.massItemPreviewLoading = false;
      }
    },

    async submitMassItemPricing() {
      this.massItemPricingError = null;
      this.massItemPricingSuccess = null;

      if (!this.massItemPricingPreview) {
        this.massItemPricingError =
          "Run Preview before confirming.";
        return;
      }

      if (
        Number(
          this.massItemPricingPreview.blocking_count || 0
        ) > 0
      ) {
        this.massItemPricingError =
          "Cannot update while blocking rows remain.";
        return;
      }

      if (!this.validateMassItemPricingInputs()) {
        return;
      }

      this.massItemSubmitting = true;

      try {
        const { data } = await axios.post(
          "/massEditCustomerItemPricing",
          {
            customer_range:
              String(
                this.massItemCustomerRange
              ).trim(),

            item_no:
              String(
                this.massItemNo
              ).trim(),

            new_price:
              Number(
                this.massItemNewPrice
              ),

            effective_dt:
              this.massItemEffectiveDt,

            dry_run: false
          }
        );

        this.massItemPricingPreview = data;

        this.massItemPricingSuccess =
          `Updated item ${data.item_no} to ` +
          `$${Number(data.new_price).toFixed(2)} ` +
          `for ${data.updated_count || 0} customer(s), ` +
          `effective ${data.effective_dt}.`;

        // If a price sheet happens to be loaded,
        // refresh it so the screen reflects the change.
        if (this.priceSheetLoaded) {
          await this.loadPriceSheet();
        }

      } catch (err) {
        const payload =
          err?.response?.data;

        this.massItemPricingError =
          payload?.error ||
          payload?.details ||
          "Failed to update mass item pricing.";

        // Backend can return the blocking preview with a 409.
        if (payload?.rows) {
          this.massItemPricingPreview = payload;
        }

      } finally {
        this.massItemSubmitting = false;
      }
    },

    async submitDeletePricing() {
      this.deletePricingError = null;
      this.deletePricingSuccess = null;
      this.deletePricingPreview = null;

      if (!this.priceSheetLoaded) return;

      if (!this.deletePricingEffectiveDt && !this.deletePricingExpirationDt) {
        this.deletePricingError = "Enter an effective date, expiration date, or both.";
        return;
      }

      this.deletePricingLoading = true;

      try {
        const payload = {
          cust_no: String(this.sheetData.cust_no).trim(),
          effective_dt: this.deletePricingEffectiveDt || null,
          expiration_dt: this.deletePricingExpirationDt || null,
          dry_run: !!this.deletePricingDryRun
        };

        const item = String(this.deletePricingItemNo || "").trim();
        if (item) payload.item_no = item;

        const { data } = await axios.post("/deleteCustomerPricingByDate", payload);

        if (this.deletePricingDryRun) {
          this.deletePricingPreview = data;
          this.deletePricingSuccess =
            `Preview found ${data.matching_rows || 0} matching row(s). Nothing deleted yet.`;
        } else {
          this.deletePricingSuccess =
            `Deleted ${data.deleted_rows || 0} pricing row(s).`;

          await this.loadPriceSheet();
        }

      } catch (err) {
        const payload = err?.response?.data;
        this.deletePricingError =
          payload?.error ||
          payload?.details ||
          "Failed to delete pricing rows.";
      } finally {
        this.deletePricingLoading = false;
      }
    },

    async confirmDeletePricing() {
      this.deletePricingDryRun = false;
      await this.submitDeletePricing();
      this.deletePricingDryRun = true;
    },


    async submitClone(forceExpire) {
      this.cloneError = null;
      this.cloneSuccess = null;

      const source = String(this.cloneSourceCust || "").trim();
      const target = String(this.cloneTargetCust || "").trim();

      // frontend validation (backend already enforces too)
      if (!source || !target) {
        this.cloneError = "Source and target customer numbers are required.";
        return;
      }
      if (!/^[0-9]+$/.test(source) || !/^[0-9]+$/.test(target)) {
        this.cloneError = "Customer numbers must be numeric.";
        return;
      }
      if (source === target) {
        this.cloneError = "Source and target customer cannot be the same.";
        return;
      }

      this.cloneLoading = true;

      try {
        const { data } = await axios.post("/cloneCustomerPricing", {
          source_cust_no: source,
          target_cust_no: target,
          force_expire: !!forceExpire
        });

        // success
        this.needsForceExpire = false;
        this.cloneSuccess = `Cloned ${data.inserted_rows} rows from ${data.source_cust_no} → ${data.target_cust_no}.` +
          (data.expired_rows ? ` Expired ${data.expired_rows} target rows first.` : "");

        // Optional: auto-load the target price sheet after clone
        this.custNo = target;
        await this.loadPriceSheet();

      } catch (err) {
        const status = err?.response?.status;
        const payload = err?.response?.data;

        // Handle your backend Option B response
        if (status === 409) {
          this.needsForceExpire = true;
          this.cloneError =
            payload?.error ||
            `Unexpired pricing exists for customer ${target}. Click "Expire & Continue" to proceed.`;
        } else {
          this.needsForceExpire = false;
          this.cloneError =
            payload?.error ||
            payload?.details ||
            "Failed to clone pricing.";
        }
      } finally {
        this.cloneLoading = false;
      }
    },

        // -------------------------
    // ✅ Popup helpers
    // -------------------------
    openPopup() {
      this.showPopup = true;
      this.popupError = null;
      this.popupLoading = false;
    },

    closePopup() {
      this.showPopup = false;
      this.popupError = null;
      this.popupLoading = false;
      this.activeTab = "item";
      // Don't clear selectedItemNo automatically — keeps context if user reopens quickly
    },

    switchTab(tab) {
      this.activeTab = tab;

      // Lazy-load tab content
      if (tab === "item" && this.selectedItemNo) {
        this.loadItemYtd();
      } else if (tab === "open") {
        this.loadOpenOrders();
      } else if (tab === "history") {
        this.loadSalesHistory();
      }
    },

    // -------------------------
    // ✅ Entry points
    // -------------------------
    async openItemModal(row, diameter) {
      console.log("CLICKED MATRIX ROW:", row);

      // ✅ With the backend change, matrix rows include: row.items[diameter] = item_no
      const itemNo =
        (row?.items && row.items[diameter]) || // ✅ preferred (group+diameter → item_no)
        row.item_no ||
        row.cd_tp_1_item_no ||
        row.Item_No ||
        row.itemNo;

      if (!itemNo) {
        this.popupError = "Could not determine item_no for this cell. (Matrix rows need items[diameter] included.)";
        this.openPopup();
        return;
      }

      this.selectedItemNo = String(itemNo).trim();
      this.popupTitle = `Clicked Price (${diameter})`;
      this.activeTab = "item";

      // Default filters when opened from a click:
      this.openOrdersItemFilter = this.selectedItemNo; // start filtered
      this.historyItemFilter = this.selectedItemNo;     // start filtered
      this.historyOffset = 0;

      this.ensureDefaultHistoryDates();

      this.openPopup();
      await this.loadItemYtd();
    },

    async openOpenOrdersModal() {
      this.popupTitle = "Open Orders";
      this.activeTab = "open";
      this.openOrdersItemFilter = null; // customer-wide from button
      this.ensureDefaultHistoryDates();
      this.openPopup();
      await this.loadOpenOrders();
    },

    async openSalesHistoryModal() {
      this.popupTitle = "Sales History";
      this.activeTab = "history";
      this.historyItemFilter = null; // customer-wide from button
      this.historyOffset = 0;
      this.ensureDefaultHistoryDates();
      this.openPopup();
      await this.loadSalesHistory();
    },

    async openSalesSummaryModal() {
      if (!this.priceSheetLoaded) return;

      this.popupTitle = "Customer Insights";
      this.activeTab = "summary";          // MUST match the tab key you use in template
      this.popupError = null;

      this.ensureDefaultHistoryDates();    // sets this.historyStart / this.historyEnd if empty

      this.openPopup();                    // opens modal first so user sees it
      await this.loadSalesSummary();        // then loads data
    },


    // -------------------------
    // ✅ Data loaders
    // -------------------------
    async loadItemYtd() {
      if (!this.priceSheetLoaded || !this.selectedItemNo) return;

      this.popupLoading = true;
      this.popupError = null;

      try {
        const { data } = await axios.get("/getCustomerItemSalesSummary", {
          params: {
            cust_no: String(this.sheetData.cust_no).trim(),
            item_no: this.selectedItemNo
          }
        });

        this.itemYtd = data;
      } catch (err) {
        console.error("loadItemYtd error:", err);
        this.popupError =
          err?.response?.data?.error ||
          err?.response?.data?.details ||
          "Failed to load item YTD.";
      } finally {
        this.popupLoading = false;
      }
    },

    async loadOpenOrders() {
      if (!this.priceSheetLoaded) return;

      this.popupLoading = true;
      this.popupError = null;

      try {
        const params = {
          cust_no: String(this.sheetData.cust_no).trim()
        };

        if (this.openOrdersItemFilter) {
          params.item_no = this.openOrdersItemFilter;
        }

        const { data } = await axios.get("/getCustomerOpenOrders", { params });
        this.openOrdersRows = data?.rows || [];
      } catch (err) {
        console.error("loadOpenOrders error:", err);
        this.popupError =
          err?.response?.data?.error ||
          err?.response?.data?.details ||
          "Failed to load open orders.";
      } finally {
        this.popupLoading = false;
      }
    },

    async loadSalesHistory() {
      if (!this.priceSheetLoaded) return;

      this.popupLoading = true;
      this.popupError = null;

      try {
        const params = {
          cust_no: String(this.sheetData.cust_no).trim(),
          start_dt: this.historyStart,
          end_dt: this.historyEnd,
          limit: this.historyLimit,
          offset: this.historyOffset
        };

        if (this.historyItemFilter) {
          params.item_no = this.historyItemFilter;
        }

        console.log("SalesHistory params:", params);


        const { data } = await axios.get("/getCustomerSalesHistory", { params });

        this.salesHistoryRows = data?.rows || [];
        this.salesHistoryTotal = Number(data?.total_rows || 0);
      } catch (err) {
        console.error("loadSalesHistory error:", err);
        this.popupError =
          err?.response?.data?.error ||
          err?.response?.data?.details ||
          "Failed to load sales history.";
      } finally {
        this.popupLoading = false;
      }
    },

    async loadSalesSummary() {
      if (!this.priceSheetLoaded) return;

      this.summaryLoading = true;
      this.popupError = null;

      try {
        const params = {
          cust_no: String(this.sheetData.cust_no).trim(),
          start_dt: this.historyStart,
          end_dt: this.historyEnd
        };

        console.log("SalesSummary params:", params);

        const { data } = await axios.get("/getCustomerSalesSummary", { params });

        // IMPORTANT: sort greatest->least by CURRENT FEET
        const rows = data?.rows || [];
        //rows.sort((a, b) => Number(b.feet_curr || 0) - Number(a.feet_curr || 0));

        this.salesSummaryRows = rows;

      } catch (err) {
        console.error("loadSalesSummary error:", err);
        this.popupError =
          err?.response?.data?.error ||
          err?.response?.data?.details ||
          "Failed to load sales summary.";
      } finally {
        this.summaryLoading = false;
      }
    },

    isValidYyyyMmDd(s) {
      if (!s) return false;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;

      const d = new Date(s + "T00:00:00");
      if (isNaN(d.getTime())) return false;

      // round-trip check
      return d.toISOString().slice(0, 10) === s;
    },

    setSummaryRange(mode) {
      const now = new Date();
      const end = now.toISOString().slice(0, 10);

      let startDate;
      if (mode === "ytd") {
        startDate = new Date(now.getFullYear(), 0, 1);
      } else {
        const days = Number(mode);
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - days);
      }

      this.historyStart = startDate.toISOString().slice(0, 10);
      this.historyEnd = end;

      // optional: auto-run when clicking chips (feels Pablo)
      this.runSalesSummary();
    },

    async runSalesSummary() {
      this.summaryDateError = null;
      this.popupError = null;

      if (!this.isValidYyyyMmDd(this.historyStart) || !this.isValidYyyyMmDd(this.historyEnd)) {
        this.summaryDateError = "Dates must be complete (YYYY-MM-DD).";
        return;
      }

      if (this.historyStart > this.historyEnd) {
        this.summaryDateError = "Start can’t be after End.";
        return;
      }

      await this.loadSalesSummary();
    },



    // -------------------------
    // ✅ Toggle filters
    // -------------------------
    async toggleOpenOrdersItemFilter() {
      if (!this.selectedItemNo) return;

      this.openOrdersItemFilter = this.openOrdersItemFilter ? null : this.selectedItemNo;
      await this.loadOpenOrders();
    },

    async toggleHistoryItemFilter() {
      if (!this.selectedItemNo) return;

      this.historyItemFilter = this.historyItemFilter ? null : this.selectedItemNo;
      this.historyOffset = 0;
      await this.loadSalesHistory();
    },

    // -------------------------
    // ✅ Paging
    // -------------------------
    async nextHistoryPage() {
      const nextOffset = this.historyOffset + this.historyLimit;
      if (nextOffset >= this.salesHistoryTotal) return;
      this.historyOffset = nextOffset;
      await this.loadSalesHistory();
    },

    async prevHistoryPage() {
      const prevOffset = this.historyOffset - this.historyLimit;
      this.historyOffset = prevOffset < 0 ? 0 : prevOffset;
      await this.loadSalesHistory();
    },

    // -------------------------
    // ✅ Formatting / defaults
    // -------------------------
    ensureDefaultHistoryDates() {
      // If already set, keep user values
      if (this.historyStart && this.historyEnd) return;

      const now = new Date();
      const yyyy = now.getFullYear();

      // start = Jan 1
      const start = new Date(yyyy, 0, 1);
      this.historyStart = start.toISOString().slice(0, 10);

      // end = today
      this.historyEnd = now.toISOString().slice(0, 10);
    },

    formatDate(v) {
      if (!v) return "";
      const d = new Date(v);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleDateString();
    },

    formatMoney(v) {
      const num = Number(v);
      if (!isFinite(num)) return "-";
      return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    formatNumber(v) {
      const num = Number(v);
      if (!isFinite(num)) return "-";
      // show decimals if needed (qty fields might be decimal)
      const hasDecimals = Math.abs(num % 1) > 0;
      return num.toLocaleString(undefined, {
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 4 : 0
      });
    },

    formatPct(v) {
      const n = Number(v || 0);
      return (n * 100).toFixed(1) + "%";
    },

    openUpdatePricingModal() {
      this.updatePricingError = null;
      this.updatePreview = null;
      this.updatePct = "";
      this.updateEffectiveDt = "";
      this.showUpdatePricingModal = true;
    },

    closeUpdatePricingModal() {
      this.showUpdatePricingModal = false;
      this.updatePreview = null;
      this.updatePricingError = null;
      this.updatePreviewLoading = false;
      this.updateSubmitting = false;
    },

    async previewUpdatePricing() {
      this.updatePricingError = null;
      this.updatePreview = null;

      if (!this.priceSheetLoaded) return;

      const pctNum = Number(String(this.updatePct || "").trim());
      if (!isFinite(pctNum)) {
        this.updatePricingError = "Percent change must be a valid number (ex: 5 or -3.25).";
        return;
      }
      if (!this.updateEffectiveDt) {
        this.updatePricingError = "Effective date is required.";
        return;
      }

      this.updatePreviewLoading = true;

      try {
        const { data } = await axios.get("/previewUpdateCustomerPricing", {
          params: {
            cust_no: String(this.sheetData.cust_no).trim(),
            pct: pctNum,
            effective_dt: this.updateEffectiveDt
          }
        });

        // If future pricing exists, preview still shows it — user sees warning.
        this.updatePreview = data;

      } catch (err) {
        const payload = err?.response?.data;
        this.updatePricingError = payload?.error || payload?.details || "Failed to preview update.";
      } finally {
        this.updatePreviewLoading = false;
      }
    },

    async submitUpdatePricing() {
      this.updatePricingError = null;

      if (!this.updatePreview) {
        this.updatePricingError = "Run Preview before confirming.";
        return;
      }

      // hard block if future pricing exists (your rule)
      if (Number(this.updatePreview.future_count || 0) > 0) {
        this.updatePricingError = `Cannot update: customer has ${this.updatePreview.future_count} future pricing rows beyond ${this.updateEffectiveDt}.`;
        return;
      }

      const pctNum = Number(String(this.updatePct || "").trim());

      this.updateSubmitting = true;

      try {
        await axios.post("/updateCustomerPricing", {
          cust_no: String(this.sheetData.cust_no).trim(),
          pct: pctNum,
          effective_dt: this.updateEffectiveDt
        });

        // refresh
        await this.loadPriceSheet();
        this.closeUpdatePricingModal();

      } catch (err) {
        const status = err?.response?.status;
        const payload = err?.response?.data;

        if (status === 409) {
          this.updatePricingError = payload?.error || "Future pricing exists. Cannot proceed.";
        } else {
          this.updatePricingError = payload?.error || payload?.details || "Failed to update pricing.";
        }
      } finally {
        this.updateSubmitting = false;
      }
    },


    exportCSV() {
      if (!this.sheetData) return;

      console.log("Export sheetData:", this.sheetData);
      console.log("Export effective_date:", this.sheetData?.effective_date);

      const customer = this.sheetData.customer || {};

      console.log("Export customer:", customer);
      console.log("Export customer keys:", Object.keys(customer));

      const cleanDate = (v) => {
        if (!v) return "";

        const s = String(v).trim();

        // Handles values like: "2026-06-08 00:00:00.000"
        const match = s.match(/^(\d{4}-\d{2}-\d{2})/);
        if (match) return match[1];

        const d = new Date(v);
        if (isNaN(d.getTime())) return s;

        return d.toISOString().slice(0, 10);
      };

      const cmpName =
        customer.cmp_name?.trim?.() ||
        customer.child_name?.trim?.() ||
        customer.InvoiceName?.trim?.() ||
        customer.parent_name?.trim?.() ||
        this.customerName ||
        "";

      console.log("Export cmpName:", cmpName);

      const city =
        customer.city?.trim?.() ||
        "";

      const custNo = String(this.sheetData.cust_no || "").trim();

      const sheetEffectiveDate = cleanDate(
        this.sheetData.effective_date ||
        this.sheetData.effective_dt ||
        ""
      );

      const rows = [];

      rows.push([
        "cmp_name",
        "city",
        "cust_no",
        "item_no",
        "price",
        "effective_date",
        "page_no",
        "product_group",
        "description",
        "pkg",
        "diameter"
      ]);

      for (const page of this.sheetData.pages || []) {
        const diameters = page.diameters || [];

        for (const row of page.rows || []) {
          for (const d of diameters) {
            const price = row.prices?.[d];

            // Skip blank matrix cells
            if (price == null || price === "") continue;

            const itemNo =
              row.items?.[d] ||
              row.item_no ||
              row.cd_tp_1_item_no ||
              row.Item_No ||
              "";

            const effectiveDate = cleanDate(
              row.effective_dates?.[d] ||
              row.effective_date ||
              row.effective_dt ||
              row.start_dt ||
              sheetEffectiveDate
            );

            rows.push([
              cmpName,
              city,
              custNo,
              String(itemNo).trim(),
              Number(price).toFixed(2),
              effectiveDate,
              page.page_no,
              row.product_group || "",
              row.description || "",
              row.pkg || "",
              d
            ]);
          }
        }
      }

      const csv = rows
        .map(r =>
          r
            .map(val => `"${String(val ?? "").replace(/"/g, '""')}"`)
            .join(",")
        )
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `price_sheet_stacked_${custNo}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>

/* ✅ PAGE BASE — Pablo black */
.price-sheet-page {
  background-color: #0b0101;
  min-height: 100vh;
  padding: 14px;
}

/* ✅ BASE CONTAINER — matches JIMScheduling */
.jim-scheduling-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #cbbfbf;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: #0b0101;
  min-height: 100vh;
}

/* ✅ Header row like JIMScheduling */
.page-header-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 10px;
}

.page-heading {
  font-family: 'Cormorant Garamond', serif;
  font-size: 36px;
  color: #cbbfbf;
  margin: 0;
  text-align: center;
}

/* ✅ MAIN LAYOUT — left controls, right scrollable content */
.main-content {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 12px;
  width: 100%;
  padding: 0 14px;
  box-sizing: border-box;
  align-items: start;
}

/* ✅ Left-side controls (classic Pablo) */
.left-side-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 80px;
}

/* ✅ Right-side workspace */
.right-side-section {
  width: 100%;
  min-width: 0;
}

/* ✅ Scrollable sheet area like MachinesView */
.sheet-scroll-container {
  max-height: 80vh;
  overflow-y: auto;
  width: 100%;
  border-radius: 10px;
  background-color: #0b0101;
  padding-right: 10px;
  box-sizing: border-box;
}

/* scrollbar subtle */
.sheet-scroll-container::-webkit-scrollbar {
  width: 10px;
}
.sheet-scroll-container::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 8px;
}
.sheet-scroll-container::-webkit-scrollbar-track {
  background: #111;
}

/* ✅ Pablo dark panel */
.panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
}

/* ✅ Controls */
.control {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  width: 100%;
}

.control label {
  font-size: 13px;
  color: #cbbfbf;
  font-weight: 600;
}

.control input {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

.control textarea {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
}

.mass-price-textarea {
  min-height: 105px;
}

/* ✅ Pablo buttons (plan-it-btn style) */
.plan-it-btn {
  border-radius: 10px;
  padding: 8px 16px;
  width: 160px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
  height: 38px;
}

.plan-it-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.plan-it-btn:disabled {
  background-color: #e5e7eb;
  color: #888;
  cursor: not-allowed;
}

.print-name-toggle {
  width: 160px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.print-name-label {
  font-weight: 600;
  font-size: 13px;
  color: #fff;
  text-align: center;
}

.print-name-buttons {
  display: flex;
  gap: 6px;
  width: 160px;
}

.print-name-btn {
  width: 77px;
  padding: 8px 0;
  font-size: 13px;
}

.print-name-btn.active {
  background-color: #dbeafe;
  border-color: #93c5fd;
  color: #1e3a8a;
}

/* ✅ Error box style (danger) */
.error-box {
  border-color: #aa4444;
  background: #2a0b0b;
  color: #fca5a5;
  width: 100%;
}

/* ✅ Summary bar (Pablo slate blue) */
.summary-bar {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #888;
  margin: 0 0 12px;
  background-color: #708090a4;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

/* summary metrics */
.summary-item {
  min-width: 140px;
  flex: 1 1 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.summary-label {
  font-size: 18px;
  opacity: 0.85;
  line-height: 1.1;
}

.summary-value {
  font-size: 24px;
  font-weight: bold;
  color: #fff;
  line-height: 1.1;
  margin-top: 2px;
}

/* ✅ Notes panel */
.notes-panel {
  margin-bottom: 12px;
}

.notes-title {
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 6px;
}

.notes-body {
  font-size: 14px;
  line-height: 1.35;
  white-space: pre-wrap;
}

/* ✅ Page wrapper */
.sheet-wrapper {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* ✅ Each page has Pablo blue-ish panel feel */
.sheet-page {
  background-color: #708090a4;
  border-radius: 10px;
  border: 1px solid #888;
}

/* ✅ Page header above each table */
.sheet-page-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
}

.sheet-page-header h2 {
  margin: 0;
  font-family: 'Cormorant Garamond', Garamond, serif;
  font-size: 22px;
  color: #fff;
}

.small-muted {
  font-size: 12px;
  opacity: 0.8;
  color: #fff;
}

/* ✅ Table scroll horizontal */
.table-scroll {
  overflow-x: auto;
  padding-bottom: 6px;
}

/* ✅ Pricing matrix table */
.price-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1000px;
}

/* base cells */
.price-table th,
.price-table td {
  border: 1px solid #333;
  padding: 6px 8px;
  font-size: 13px;
  text-align: center;
}

/* header row (dark) */
.price-table th {
  background-color: #0b0101;
  color: #cbbfbf;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 3;
}

/* sticky left column */
.sticky-col {
  position: sticky;
  left: 0;
  z-index: 4;
  background: #0b0101;
  min-width: 120px;
  text-align: left !important;
  color: #cbbfbf;
}

/* row hover = MachinesView vibe */
.price-table tbody tr:hover td {
  background-color: #5595b7c2;
  color: #fff;
}

/* columns */
.group-cell {
  font-weight: bold;
}

.desc-col {
  min-width: 220px;
  text-align: left !important;
}

.pkg-col {
  min-width: 60px;
}

.diam-col,
.price-cell {
  min-width: 55px;
}

.dash {
  opacity: 0.6;
}

.empty-row {
  padding: 14px;
  font-size: 14px;
  opacity: 0.75;
  text-align: center;
}

/* ✅ Freight multiplier row */
.freight-row td {
  background: #0b0101;
  color: #cbbfbf;
  font-weight: bold;
}

.freight-cell {
  text-align: center !important;
  padding: 10px 12px !important;
}

.freight-line {
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  gap: 10px;
  text-align: center;
}

.freight-label {
  opacity: 0.85;
  font-weight: 600;
}

.freight-value {
  font-weight: bold;
  color: #fff;
}

.sep {
  opacity: 0.5;
  margin: 0 4px;
}

/* ✅ Footer */
.print-footer {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #333;
  color: #fff;
  font-size: 12px;
  line-height: 1.35;
}

.footer-text {
  white-space: pre-wrap;
}

.footer-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}

.footer-line {
  font-style: italic;
  opacity: 0.9;
  margin-bottom: 6px;
}

.footer-row {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 10px;
}

.footer-row .k {
  font-weight: bold;
  white-space: nowrap;
}

.footer-row .v {
  opacity: 0.95;
}

/* ✅ Popup modal styling (matches JIMScheduling / MachineScheduling) */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup-content {
  background-color: #0b0101;
  padding: 10px 20px;
  border-radius: 8px;
  text-align: center;
  max-width: 95vw;
  max-height: 90vh;
  overflow: auto;
  border: 1px solid #888;
  color: #cbbfbf;
}

/* ✅ Header + close button (same vibe as your other modals) */
.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.close-btn {
  background: transparent;
  border: none;
  color: #cbbfbf;
  font-size: 20px;
  cursor: pointer;
  font-weight: bold;
}

/* ✅ Clickable prices */
.clickable-price {
  cursor: pointer;
}
.clickable-price:hover {
  text-decoration: underline;
}

/* ✅ Popup modal styling (matches LogisticsPage.vue) */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup-content {
  background-color: #0b0101;
  padding: 10px 20px;
  border-radius: 8px;
  text-align: left;
  max-width: 95vw;
  max-height: 90vh;
  overflow: auto;
  border: 1px solid #888;
  color: #cbbfbf;
}

/* ✅ Header + close button */
.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 12px;
}

.close-btn {
  background: transparent;
  border: none;
  color: #cbbfbf;
  font-size: 20px;
  cursor: pointer;
  font-weight: bold;
}

/* ✅ Tabs */
.popup-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.popup-tab-btn {
  border-radius: 10px;
  padding: 6px 12px;
  background-color: #111;
  border: 1px solid #444;
  color: #cbbfbf;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
}

.popup-tab-btn.active {
  background-color: #708090a4;
  border-color: #888;
  color: #fff;
}

.popup-tab-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ✅ Subheader row in modal */
.popup-subheader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

/* ✅ Metrics grid */
.popup-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(140px, 1fr));
  gap: 10px;
}

.metric {
  border: 1px solid #333;
  border-radius: 8px;
  padding: 8px 10px;
  background: #0f141c;
}

.metric .k {
  font-size: 12px;
  opacity: 0.85;
}

.metric .v {
  font-size: 18px;
  font-weight: bold;
  margin-top: 2px;
  color: #fff;
}

/* ✅ Small buttons inside modal */
.small-btn {
  border-radius: 10px;
  padding: 6px 10px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  height: 34px;
}

.small-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.small-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ✅ Modal table formatting (matches LogisticsPage.vue) */
.popup-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.popup-table th,
.popup-table td {
  border: 1px solid #333;
  padding: 8px;
  font-size: 13px;
  text-align: left;
}

.popup-table th {
  background: #111827;
  color: #cbbfbf;
  font-weight: bold;
}

.popup-table tr:nth-child(even) td {
  background: #0f141c;
}

/* ✅ Sales history layout helpers */
.history-controls {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.history-paging {
  display: flex;
  gap: 8px;
}

.pablo-summary-bar{
  display:flex;
  justify-content:space-between;
  align-items:flex-end;
  gap:14px;
  padding:12px;
  border-radius:14px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
}

.pablo-chip-row{
  display:flex;
  gap:8px;
  align-items:center;
  flex-wrap:wrap;
  margin-bottom:10px;
}

.pablo-chip{
  padding:6px 10px;
  border-radius:999px;
  border:1px solid rgba(255,255,255,0.14);
  background: rgba(255,255,255,0.06);
  font-size:12px;
  cursor:pointer;
  transition: transform 0.08s ease;

  color: #cbbfbf;      /* ✅ matches .popup-tab-btn */
  font-family: inherit;
  font-weight: 600;    /* optional: matches the tabs */
}

.pablo-chip:active{ transform: scale(0.98); }

.pablo-pill{
  padding:6px 10px;
  border-radius:999px;
  font-size:12px;
  border:1px solid rgba(255,255,255,0.14);
  background: rgba(255,255,255,0.04);
  opacity:0.9;
}
.pablo-pill.ok{
  border-color: rgba(120,255,170,0.35);
}
.pablo-pill.bad{
  border-color: rgba(255,140,140,0.35);
}

.pablo-date-row{
  display:flex;
  gap:12px;
  align-items:flex-end;
  flex-wrap:wrap;
}

.pablo-inline-error{
  font-size:12px;
  opacity:0.9;
  padding:6px 10px;
  border-radius:10px;
  border:1px solid rgba(255,140,140,0.35);
  background: rgba(255,80,80,0.08);
}

.pablo-go-btn{
  min-width:92px;
  height:44px;
  border-radius:14px;
  font-weight:700;
  letter-spacing:0.5px;
}

.summary-totals-bar{
  display:flex;
  gap:12px;
  flex-wrap:wrap;
  align-items:stretch;
  margin-top:10px;
  margin-bottom:10px;
  padding:10px 12px;
  border-radius:14px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
}

.summary-totals-bar .tot{
  min-width: 120px;
  padding:8px 10px;
  border-radius:12px;
  background: rgba(0,0,0,0.25);
  border: 1px solid rgba(255,255,255,0.08);
}

.summary-totals-bar .k{
  font-size:11px;
  letter-spacing:0.4px;
  opacity:0.75;
  margin-bottom:4px;
}

.summary-totals-bar .v{
  font-size:14px;
  font-weight:700;
  color:#fff;
}

.pricing-date-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 14px;
  color: #cbbfbf;
}

.success-box {
  border-color: #4ade80;
  background: #052e16;
  color: #bbf7d0;
  width: 100%;
}

.danger-btn {
  background-color: #fee2e2;
  border-color: #fca5a5;
  color: #7f1d1d;
}

.danger-btn:hover:not(:disabled) {
  background-color: #fecaca;
  border-color: #f87171;
}





/* ✅ PRINT STYLING */
@media print {

  /* Hide controls */
  .left-side-section,
  .error-box {
    display: none !important;
  }

  /* Full width print */
  .main-content {
    display: block !important;
    padding: 0 !important;
  }

  /* Each sheet page = printed page */
  .sheet-page {
    page-break-after: always;
    break-after: page;
    display: flex !important;
    flex-direction: column !important;
    min-height: 95vh !important;
    background: white !important;
    border: none !important;
  }

  /* table expands */
  .table-scroll {
    flex: 1 1 auto !important;
    overflow: visible !important;
  }

  /* footer bottom */
  .print-footer {
    margin-top: auto !important;
    border-top: 1px solid #999 !important;
    color: black !important;
  }

  .print-footer * {
    color: black !important;
  }

  /* Convert all panels white */
  .jim-scheduling-container,
  .panel,
  .summary-bar {
    background: white !important;
    color: black !important;
    border: none !important;
  }

  /* header table */
  .price-table th {
    background: #eee !important;
    color: black !important;
  }

  .sticky-col {
    background: white !important;
    color: black !important;
  }

  /* Freight row print readability */
  .freight-row td {
    background: #f3f3f3 !important;
    color: black !important;
  }

  .freight-value {
    color: black !important;
  }
}

</style>
